class VariantsDrawer extends HTMLElement {
  constructor() {
    super();
    this.colourStateInitialized = false;
  }

  connectedCallback() {
    if (this.colourStateInitialized) return;
    this.colourStateInitialized = true;
    this.querySelectorAll('.variants-drawer__body .shop-the-look__colour-variant').forEach((variant) => {
      variant.addEventListener('click', this.updateColourState.bind(this));
    });
  }

  updateColourState(e) {
    const colourName = e.currentTarget.querySelector('span.visually-hidden, span.visibility-hidden')?.innerText?.trim();
    const swatchVar = e.currentTarget.dataset.swatchVar;
    const swatchColor = swatchVar ? getComputedStyle(document.documentElement).getPropertyValue(swatchVar).trim() : '';
    const swatchPreview = this.querySelector('.button__colours-preview .color-swatches__swatch');
    const namePreview = this.querySelector('.button__colours-preview .color-swatches__name') || this.querySelector('.button__colours-preview .color-swatches span:last-child');

    if (swatchPreview) {
      if (swatchVar) swatchPreview.style.setProperty('--swatch-bg-color', `var(${swatchVar})`);
      if (swatchColor) {
        swatchPreview.style.backgroundColor = `rgb(${swatchColor})`;
        swatchPreview.style.border = swatchColor === '255 255 255' ? '1px solid #000' : 'unset';
      }
      if (colourName) {
        swatchPreview.title = colourName;
        const hiddenLabel = swatchPreview.querySelector('.visibility-hidden, .visually-hidden');
        if (hiddenLabel) hiddenLabel.textContent = colourName;
      }
    }
    if (namePreview && colourName) {
      namePreview.innerText = colourName;
    }
  }

  showDrawer() {
    // set variants-drawer__body aria-hidden to false
    const drawerBody = this.querySelector('.variants-drawer__body');
    if (drawerBody) {
      drawerBody.setAttribute('aria-hidden', 'false');
    }
  }

  hideDrawer() {
    // set variants-drawer__body aria-hidden to true
    const drawerBody = this.querySelector('.variants-drawer__body');
    if (drawerBody) {
      drawerBody.setAttribute('aria-hidden', 'true');
    }

    this.dataset.view = '';
  }

  hideAllDrawerContent() {
    // get all drawer content
    const drawerContents = this.querySelectorAll('.variants-drawer__content > div[aria-hidden]');
    if (!drawerContents) {
      return;
    }

    // set aria-hidden to true
    drawerContents.forEach((drawerContent) => {
      drawerContent.setAttribute('aria-hidden', 'true');
    });

    this.classList.remove('variants-drawer--viewAll')
  }

  showDrawerContent(id) {
    if (!id) return;
    const drawerContent = this.querySelector(`[id="${id}"]`);
    if (!drawerContent) {
      return;
    }

    // set aria-hidden to false
    drawerContent.setAttribute('aria-hidden', 'false');
  }

  toggleViewAll(drawerElement) {
    drawerElement.classList.toggle('variants-drawer--viewAll');
  }
}

class VariantsDrawerButton extends VariantsDrawer {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.imageContainer = document.querySelector('.product__media-list');
    this.drawerInitialized = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.buttons = this.parentElement?.querySelectorAll('variants-drawer-button');
    this.drawerElement = this.closest('variants-drawer');
    this.ariaControls = this.getAttribute('aria-controls');

    if (this.drawerElement && !this.drawerInitialized) {
      this.drawerInitialized = true;
      this.handleDrawer();
    }

    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  resetButtons() {
    this.buttons?.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  updateDatasetViewAttribute(view) {
    if (!this.drawerElement) return;
    this.drawerElement.dataset.view = view;
  }

  handleClick() {
    if (!this.drawerElement) return;
    const allDrawers = document.querySelectorAll('variants-drawer');

    allDrawers.forEach((drawer) => {
      if (drawer !== this.drawerElement) {
        drawer.hideDrawer();
        const buttons = drawer.querySelectorAll('variants-drawer-button');
        if (buttons) {
          buttons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        }
      }

      if (document.querySelector('body').getAttribute('data-bundle-product') === 'true') {
        this.handleImageChange(drawer);
      }
    });

    if (this.getAttribute('aria-expanded') === 'true') {
      this.drawerElement.hideDrawer();

      this.resetButtons();
      this.setAttribute('aria-expanded', 'false');
      return;
    }

    // show drawer and its content
    this.drawerElement.showDrawer();
    this.resetButtons();
    this.setAttribute('aria-expanded', 'true');

    if (!this.ariaControls) {
      return;
    }

    this.drawerElement.hideAllDrawerContent();
    this.drawerElement.showDrawerContent(this.ariaControls);
    this.updateDatasetViewAttribute(this.dataset.view);
  }

  removeWidthParameter(url) {
    return url.replace(/&width=\d+/, '');
  }
  
  createSrcset(baseImageUrl, widths) {
    return widths.map(width => `${baseImageUrl}&width=${width} ${width}w`).join(', ');
  }
  
  createNewListItem(baseImageUrl, srcset, sizes, index) {
    const newListItem = document.createElement('li');
    newListItem.classList.add('scroll-snap__item', 'product__media-item', 'product__media-item--variant');
    if (index === 0) {
      newListItem.classList.add('is-active');
    }
  
    const newImageContainer = document.createElement('div');
    newImageContainer.classList.add('product-media-container', 'media-type-image', 'media-fit-contain', 'global-media-settings', 'gradient', 'constrain-height');
  
    const imagePictureContainer = document.createElement('div');
    imagePictureContainer.classList.add('product__media', 'media', 'media--transparent');
  
    const imagePicture = document.createElement('picture');
    const imageSource = document.createElement('source');
    imageSource.setAttribute('srcset', srcset);
    imageSource.setAttribute('sizes', sizes);
    imageSource.setAttribute('media', '(min-width: 1024px)');
    imagePicture.appendChild(imageSource);
  
    const imageImg = document.createElement('img');
    imageImg.setAttribute('src', baseImageUrl);
    imagePicture.appendChild(imageImg);
  
    imagePictureContainer.appendChild(imagePicture);
    newImageContainer.appendChild(imagePictureContainer);
    newListItem.appendChild(newImageContainer);
  
    return newListItem;
  }
  
  handleVariantClick(event, newListItem, imageContainer) {
    const existingActiveItem = imageContainer.querySelector('.is-active');
    if (existingActiveItem) {
      imageContainer.replaceChild(newListItem, existingActiveItem);
    } else {
      imageContainer.prepend(newListItem);
    }
    newListItem.classList.add('is-active');
  }
  
  handleImageChange(drawer) {
    const drawerColourVariants = drawer.querySelectorAll('.shop-the-look__colour-variant');
  
    if (drawerColourVariants) {
      drawerColourVariants.forEach((drawerColourVariant, index) => {
        let colourImage = drawerColourVariant.querySelector('span').getAttribute('data-variant-image');
        
        const baseImageUrl = this.removeWidthParameter(colourImage);
        const widths = [375, 550, 750, 800, 850, 900, 1100, 1500, 1780, 2000, 3000, 3840];
        const srcset = this.createSrcset(baseImageUrl, widths);
        const sizes = "(min-width: 750px) 50vw, 100vw";
  
        const newListItem = this.createNewListItem(baseImageUrl, srcset, sizes, index);
  
        drawerColourVariant.addEventListener('click', event => this.handleVariantClick(event, newListItem, this.imageContainer));
      });
    }
  }

  handleDrawer() {
    const drawerContent = this.drawerElement.querySelector(`[id="${this.ariaControls}"]`);
    if (!drawerContent) return;

    drawerContent.addEventListener('click', event => {
      if (event.target.closest('.shop-the-look__colour-variant') || event.target.closest('.card-variant-selector')) {
        this.handleClick();
      }
    });
  }
}

class VariantsDrawerRevealButton extends VariantsDrawer {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.minSwatchesForViewAllButton = 6;
  }

  connectedCallback() {
    super.connectedCallback();
    this.swatches = this.parentElement?.parentElement?.querySelectorAll('product-group-swatch');

    if (this.swatches && this.swatches.length > this.minSwatchesForViewAllButton) {
      this.style.display = 'flex';
    }

    this.drawerElement = this.closest('variants-drawer');
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick() {
    if (!this.drawerElement) return;
    this.toggleViewAll(this.drawerElement);
  }
}

window.customElements.define('variants-drawer', VariantsDrawer);
window.customElements.define('variants-drawer-button', VariantsDrawerButton);
window.customElements.define('variants-drawer-reveal-button', VariantsDrawerRevealButton);
